import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, ButtonProps } from "reactstrap";
import * as Messages from "../../../Messages";
import { basePath, exampleJob } from "../../../settings";
import { JobApi } from "../../../api";
import { SubmissionRequestWithEmail } from "../../../api/openapi-generator";
import { LicenseTypeEnum } from "../types/license";
import { useInputContext } from "../context/input-context";

interface Props {
  disabled?: boolean,
  buttonProps?: Omit<ButtonProps, "disabled" | "onClick">,
  children?: React.ReactNode,
  validate? (): boolean,
  beforeSubmit? (): boolean,
}

function useSubmissionRequest(): SubmissionRequestWithEmail | null {
  
  const {
    emailField,
    licenseForm,
    targetSelected,
    mutations,
    vinaJobDesiredRun,
  } = useInputContext();

  return useMemo(() => {
    const academic = licenseForm.license === LicenseTypeEnum.ACADEMIC;
    const commercial = licenseForm.license === LicenseTypeEnum.COMMERCIAL;
    return targetSelected !== null && mutations.length > 0 ? {
      email: emailField,
      position: mutations[0].position,
      targetAA: mutations[0].units[0],
      protein: targetSelected.codeAscii,
      license: {
        academic: academic,
        rosetta:  commercial ? licenseForm.commercial.rosetta : undefined,
        foldx:    commercial ? licenseForm.commercial.foldx : undefined,
        modeller: undefined,
      },
      vinaJobDesiredRun: vinaJobDesiredRun,
    } : null;
  }, [
    emailField,
    licenseForm,
    targetSelected,
    mutations,
    vinaJobDesiredRun,
  ]);
  
}

const RunJobButton = ({beforeSubmit, validate, ...props}: Props) => {

  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  const history = useHistory();

  const submissionRequest = useSubmissionRequest();

  const isExampleJob = Boolean(
    submissionRequest &&
    exampleJob.code === submissionRequest.protein &&
    exampleJob.position === submissionRequest.position &&
    exampleJob.targetAA === submissionRequest.targetAA
  );

  const showResults = useCallback((id: string) => {
    history.push(`${basePath}/job/${id}`);
  }, [history]);

  const handleRunJobClick = useCallback(() => {
    if (beforeSubmit?.() === false) {
      // do not run job, beforeSubmit should report validity
    }
    else if (validate?.() === false) {
      console.error('invalid input');
    }
    else if (!submissionRequest) {
      console.error('no submission request');
    }
    else {
      // run a job normally:
      setSubmitInProgress(true);
      setSubmitFailed(false);
      JobApi.submit({submissionRequestWithEmail: {
        ...submissionRequest,
          ...(isExampleJob ? {
            requireTemplateId: true,
            reuseUnfinishedJob: true,
          } : {}),
      }})
        .then(id => {
          showResults(id);
        })
        .catch(err => {
          setSubmitFailed(true);
          console.error(err);
        })
        .finally(() => {
          setSubmitInProgress(false);
        })
    }
  }, [beforeSubmit, validate, isExampleJob, submissionRequest, showResults]);

  const btnProps = {
    color: "primary",
    size: "lg",
    ...(props.buttonProps ?? {}),
    onClick: handleRunJobClick,
    disabled: props.disabled || submitInProgress || !submissionRequest,
  };

  return <>
    <Button {...btnProps}>
      {props.children}
    </Button>

    {submitInProgress && <span style={{ marginLeft: '1rem' }}>{Messages.spinnerIcon}</span>}
    {submitFailed && <div className="ll-submission-failed-msg">{Messages.SUBMISSION_FAILED()}</div>}

  </>;
}

export default RunJobButton;
